import {Location} from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {BehaviorSubject, catchError, combineLatest, defer, EMPTY, filter, finalize, Observable, Subject, switchMap, takeUntil, tap} from 'rxjs';
import { CompanyService } from '@modules/company/services/company.service';
import { CompanyEventService } from '@modules/company/services/company-event.service';
import { Company } from '@modules/company/models/company.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CompanyHelper } from '@shared/helpers/company.helper';
import { ACTIVE_USER } from '@modules/auth/providers/auth.provider';
import { User } from '@modules/user/models/user.model';
import {
  CompanyStatusEnum,
  UpdateCompanyBasicInfoMutation,
} from '@generated/graphql';
import { APP_ROUTES, AppRoutes } from '@config/app-routes.config';
import { MatDialog } from '@angular/material/dialog';
import { CreateCompanyProfileDialogComponent } from '@shared/component/company/dialog/create-company-profile-dialog/create-company-profile-dialog.component';
import { BusinessPlanService } from '@app/modules/account-settings/pages/business-profile/services/business-plan.service';
import {ActivatedRoute, Router} from "@angular/router";
import {MatTabChangeEvent} from '@angular/material/tabs';
import { ScreenSpinnerService } from '@shared/component/full-screen-spinner/screen-spinner.service';
import { SnackBarService } from '@shared/services/snack-bar.service';


export enum CompanyProfileTab {
  Information = 0,
  Kyc = 1,
  Documents = 2,
  Subscriptions = 3
}

export const COMPANY_PROFILE_TAB_QUERY_PARAM = 'tab';
export const COMPANY_PROFILE_TAB_QUERY_PARAM_VALUES: Record<CompanyProfileTab, string> = {
  [CompanyProfileTab.Information]: 'company-info',
  [CompanyProfileTab.Kyc]: 'kyc-ubo',
  [CompanyProfileTab.Documents]: 'documentations',
  [CompanyProfileTab.Subscriptions]: 'subscriptions',
};

const COMPANY_PROFILE_TAB_QUERY_PARAM_ALIASES: Record<string, CompanyProfileTab> = {
  '0': CompanyProfileTab.Information,
  '1': CompanyProfileTab.Kyc,
  '2': CompanyProfileTab.Documents,
  '3': CompanyProfileTab.Subscriptions,
  information: CompanyProfileTab.Information,
  info: CompanyProfileTab.Information,
  'company-info': CompanyProfileTab.Information,
  kyc: CompanyProfileTab.Kyc,
  'kyc-ubo': CompanyProfileTab.Kyc,
  documents: CompanyProfileTab.Documents,
  documentations: CompanyProfileTab.Documents,
  subscriptions: CompanyProfileTab.Subscriptions,
};

@UntilDestroy()
@Component({
  selector: 'app-company-profile-page',
  templateUrl: './company-profile-page.component.html',
  styleUrls: ['./company-profile-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyProfilePageComponent implements OnInit, OnDestroy {
  public companyHelper: CompanyHelper;
  protected readonly CompanyStatusEnum = CompanyStatusEnum;
  readonly CompanyProfileTab = CompanyProfileTab;

  submitting$ = new BehaviorSubject(false);

  private _selectedIndex$ = new BehaviorSubject<number>(0);
  private _kycTabLoading$ = new BehaviorSubject(false);
  private readonly tabRefresh$ = new Subject<{ companyId: string; tab: CompanyProfileTab }>();
  private visibleTabs: CompanyProfileTab[] = [
    CompanyProfileTab.Information,
    CompanyProfileTab.Kyc,
    CompanyProfileTab.Documents,
  ];
  private activeCompanyId: string | null = null;
  private selectedTab: CompanyProfileTab = CompanyProfileTab.Information;


  public get businessPlan$() {
    return this._businessPlanService.businessPlan$;
  }

  public get businessPlanInitialized$() {
    return this._businessPlanService.businessPlanInitialized$;
  }

  get selectedIndex$ () {
    return this._selectedIndex$.asObservable()
  }

  get kycTabLoading$() {
    return this._kycTabLoading$.asObservable();
  }

  // get documentTemplateTableData$ (): Observable<DocumentTemplateTableComponentData> {
  //   return this.companyService.activeEntity$.pipe(
  //     map(company => company ? {
  //         templateData: company,
  //         hasDocumentTemplateId: company.id,
  //         hasDocumentTemplateType: company._model
  //     } : null)
  //   )
  // }

  constructor(
    @Inject(APP_ROUTES) public readonly appRoutes: AppRoutes,
    @Inject(ACTIVE_USER) protected readonly activeUser$: Observable<User>,
    private readonly companyEventService: CompanyEventService,
    private readonly companyService: CompanyService,
    private readonly cdr: ChangeDetectorRef,
    private readonly dialog: MatDialog,
    private readonly updateCompanyBasicInfoMutation: UpdateCompanyBasicInfoMutation,
    private readonly _businessPlanService: BusinessPlanService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly location: Location,
    private readonly screenSpinnerService: ScreenSpinnerService,
    private readonly snackBarService: SnackBarService,
  ) { }

  ngOnInit(): void {
    this.companyHelper = CompanyHelper.getInstance();
    this.setupTabRefresh();

    this.companyService.activeEntity$
      .pipe(
        untilDestroyed(this),
        tap((company) => {
          this.companyEventService.setCompany(company);

          this.companyEventService.setCompanySecretaries(
            company.companySecretaries,
          );

          this.companyEventService.setCompanyAuditors(company.companyAuditors);

          this.cdr.markForCheck();
        }),
      )
      .subscribe();

    this.companyEventService
      .companySecretaryCreateEvent$()
      .pipe(untilDestroyed(this))
      .subscribe();

    this.companyEventService
      .companySecretaryUpdateEvent$()
      .pipe(untilDestroyed(this))
      .subscribe();

    this.companyEventService
      .companySecretaryDeleteEvent$()
      .pipe(untilDestroyed(this))
      .subscribe();

    this.companyEventService
      .companyAuditorCreateEvent$()
      .pipe(untilDestroyed(this))
      .subscribe();

    this.companyEventService
      .companyAuditorUpdateEvent$()
      .pipe(untilDestroyed(this))
      .subscribe();

    this.companyEventService
      .companyAuditorDeleteEvent$()
      .pipe(untilDestroyed(this))
      .subscribe();

    combineLatest([
      this.route.paramMap,
      this.route.queryParamMap,
      this.businessPlan$,
      this.businessPlanInitialized$,
    ])
      .pipe(
        untilDestroyed(this),
        tap(([routeParams, queryParams, businessPlan, businessPlanInitialized]) => {
          const companyId = routeParams.get('id');

          if (companyId && companyId !== this.activeCompanyId) {
            this.activeCompanyId = companyId;
          }

          this.visibleTabs = this.getVisibleTabs(businessPlan);

          const currentBrowserTabParam = this.getCurrentBrowserTabParam() ?? queryParams.get(COMPANY_PROFILE_TAB_QUERY_PARAM);
          const requestedTab = this.resolveTab(currentBrowserTabParam);
          const canOpenRequestedTab = requestedTab !== CompanyProfileTab.Subscriptions || businessPlanInitialized;
          const effectiveRequestedTab = canOpenRequestedTab
            ? requestedTab
            : null;
          const requestedTabIsHidden = effectiveRequestedTab != null
            && !this.visibleTabs.includes(effectiveRequestedTab);

          const selectedTab = effectiveRequestedTab && this.visibleTabs.includes(effectiveRequestedTab)
            ? effectiveRequestedTab
            : this.visibleTabs[0];

          this.selectedTab = selectedTab;
          this._selectedIndex$.next(this.visibleTabs.indexOf(selectedTab));

          if (!currentBrowserTabParam) {
            this.syncTabQueryParam(selectedTab);
            return;
          }

          if (requestedTabIsHidden) {
            this.syncTabQueryParam(selectedTab);
            this.refreshSelectedTab(selectedTab);
          }
        }),
      )
      .subscribe();
  }

  public get company$(): Observable<Company> {
    return this.companyEventService.company$;
  }

  onTabChange(event: MatTabChangeEvent): void {
    const selectedTab = this.visibleTabs[event.index];

    if (selectedTab == null) {
      return;
    }

    this.selectedTab = selectedTab;
    this._selectedIndex$.next(event.index);
    this.refreshSelectedTab(selectedTab);
    this.syncTabQueryParam(selectedTab);
  }

  isSelectedTab(tab: CompanyProfileTab): boolean {
    return this.selectedTab === tab;
  }

  private refreshCompany(): void {
    this.companyService
      .refreshActiveInfoEntity$(this.companyEventService.company.id)
      .subscribe();
  }

  public onCreateCompanyProfile() {
    const createCompanyProfileDialog = this.dialog.open(
      CreateCompanyProfileDialogComponent,
      {
        autoFocus: false,
        width: '60%',
        maxWidth: '65%',
        data: {
          company: this.companyEventService.company,
        },
      },
    );

    createCompanyProfileDialog.afterClosed().subscribe((result) => {
      if (result === true) {
        this.refreshCompany();
      }
    });
  }

  archiveCompanyHandler(): void {
    this.submitting$.next(true);

    this.updateCompanyBasicInfoMutation
      .mutate({
        id: this.companyEventService.company.id,
        input: {
          status: CompanyStatusEnum.Archived,
        },
      })
      .pipe(
        catchError((error) => {
          this.submitting$.next(false);
          return error;
        }),
      )
      .subscribe(() => {
        this.refreshCompany();
        this.submitting$.next(false);
      });
  }

  ngOnDestroy(): void {
    this.submitting$.complete();
  }

  private getVisibleTabs(businessPlan?: { isPremium: () => boolean } | null): CompanyProfileTab[] {
    const tabs = [
      CompanyProfileTab.Information,
      CompanyProfileTab.Kyc,
      CompanyProfileTab.Documents,
    ];

    if (businessPlan?.isPremium()) {
      tabs.push(CompanyProfileTab.Subscriptions);
    }

    return tabs;
  }

  private resolveTab(tabParam: string | null): CompanyProfileTab | null {
    if (!tabParam) {
      return null;
    }

    return COMPANY_PROFILE_TAB_QUERY_PARAM_ALIASES[tabParam.toLowerCase()] ?? null;
  }

  private syncTabQueryParam(tab: CompanyProfileTab): void {
    const tabParam = COMPANY_PROFILE_TAB_QUERY_PARAM_VALUES[tab];

    if (this.getCurrentBrowserTabParam() === tabParam) {
      return;
    }

    const url = this.router.serializeUrl(this.router.createUrlTree([], {
      relativeTo: this.route,
      queryParams: {
        ...this.route.snapshot.queryParams,
        [COMPANY_PROFILE_TAB_QUERY_PARAM]: tabParam,
      },
      fragment: this.route.snapshot.fragment ?? undefined,
    }));

    this.location.replaceState(url);
  }

  private getCurrentBrowserTabParam(): string | null {
    const queryString = this.location.path(true).split('?')[1]?.split('#')[0] ?? '';

    return new URLSearchParams(queryString).get(COMPANY_PROFILE_TAB_QUERY_PARAM);
  }

  private refreshSelectedTab(tab: CompanyProfileTab): void {
    const companyId = this.activeCompanyId;
    if (!companyId) {
      return;
    }

    this.tabRefresh$.next({ companyId, tab });
  }

  private loadTabEntity$(tab: CompanyProfileTab, companyId: string): Observable<Company> | null {
    if (tab === CompanyProfileTab.Information) {
      return this.companyService.refreshActiveInfoEntity$(
        companyId,
        { swallowError: false },
      );
    }

    if (tab === CompanyProfileTab.Kyc) {
      return this.companyService.refreshActiveKycEntity$(
        companyId,
        { swallowError: false },
      );
    }

    if (tab === CompanyProfileTab.Subscriptions) {
      return this.companyService.refreshActiveSubscriptionsEntity$(
        companyId,
        { swallowError: false },
      );
    }

    return null;
  }

  private setupTabRefresh(): void {
    this.tabRefresh$.pipe(
      untilDestroyed(this),
      switchMap(({ companyId, tab }) => {
        const loadTabEntity$ = this.loadTabEntity$(tab, companyId);
        if (!loadTabEntity$) {
          return EMPTY;
        }

        return defer(() => {
          this.setTabLoading(tab, true);
          this.screenSpinnerService.setLoading(true);

          return loadTabEntity$.pipe(
            takeUntil(
              this.route.paramMap.pipe(
                filter((params) => params.get('id') !== companyId),
              ),
            ),
            catchError((error) => {
              if (this.activeCompanyId === companyId) {
                this.snackBarService.pushErrorAlert('Failed to load this tab. Please try again.');
              }

              console.error(error);

              return EMPTY;
            }),
            finalize(() => {
              this.setTabLoading(tab, false);
              this.screenSpinnerService.setLoading(false);
              this.cdr.markForCheck();
            }),
          );
        });
      }),
    ).subscribe();
  }

  private setTabLoading(tab: CompanyProfileTab, value: boolean): void {
    if (tab === CompanyProfileTab.Kyc) {
      this._kycTabLoading$.next(value);
    }
  }
}
